import type { CashierTradingAccount } from "@/api/cashier";
import { usePlatformIcon } from "@/components/account-list/usePlatformIcon";
import { CurrencyTag } from "@/components/currency/currency-tag/CurrencyTag";
import {
  SelectableCard,
  type SelectableCardProps,
} from "@/components/selectable-card/SelectableCard";
import { useFormatAmount } from "@/hooks/useFormatAmount";

/**
 * Represents an account card component that displays details about a trading account,
 * including balance, platform, account type, and currency information. It is a customized
 * selectable card with additional account-specific data.
 *
 * @param {Object} params - Configuration object for the AccountCard component, properties inherited from the SelectableCardProps minus the "id" property.
 * @param {CashierTradingAccount} params.account - The trading account information to display on the card.
 * @param {number} [params.balance] - Optional balance value to override the account's balance. If not provided, the account's default balance will be formatted and used.
 */
export const AccountCard = ({
  account,
  balance,
  ...props
}: { account: CashierTradingAccount; balance?: number } & Omit<
  SelectableCardProps,
  "id"
>) => {
  const { formatAmount, formatBankingAmount } = useFormatAmount();
  const icon = usePlatformIcon(account.tradingPlatform);
  const accountBalance =
    typeof account.balance === "number"
      ? formatAmount(balance, account.balance)
      : formatBankingAmount(account.balance);
  return (
    <SelectableCard
      data-testid="account-card"
      id={`${account.id}`}
      icon={icon}
      iconPadding="xs"
      title={accountBalance}
      topText={`${account.tradingPlatform} - ${account.accountLoginId} - ${account.accountType}`}
      subText={account.accountNickname ?? account.accountName}
      tag={<CurrencyTag currencyCode={account.balance.code} />}
      {...props}
    />
  );
};
